<script setup lang="ts">
import { formatCidr, parseCidr } from '@it-tools/core-net'
import { AdSlot, AppShell, localizedPath } from '@it-tools/shell'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import NetworkInput from './components/NetworkInput.vue'
import SubnetTable from './components/SubnetTable.vue'
import ToolbarControls from './components/ToolbarControls.vue'
import { DEFAULT_NETWORK, useDesign } from './model/useDesign'
import { readLocationState, replaceLocationState } from './model/urlState'

const { t, locale } = useI18n()
const calculator = useDesign()

// Keep the reader in their language when they follow the link.
const referenceHref = computed(() => localizedPath('/cloud-reserved-ip-addresses/', locale.value))
const networkText = ref(DEFAULT_NETWORK)
const shareNotice = ref(false)

// A shared link restores the whole design; a malformed one silently falls back to
// the default so the user always lands on a working calculator.
onMounted(() => {
  const restored = readLocationState()
  if (!restored) return
  calculator.load(restored.design, restored.cloudMode)
  networkText.value = formatCidr(restored.design.tree.root)
})

watch(
  [calculator.design, calculator.cloudMode],
  () => {
    replaceLocationState({
      design: calculator.design.value,
      cloudMode: calculator.cloudMode.value,
    })
  },
  { deep: false },
)

async function share(): Promise<void> {
  replaceLocationState({
    design: calculator.design.value,
    cloudMode: calculator.cloudMode.value,
  })
  try {
    await navigator.clipboard.writeText(window.location.href)
    shareNotice.value = true
    setTimeout(() => (shareNotice.value = false), 2000)
  } catch {
    // Clipboard access can be denied; the URL bar already holds the link.
  }
}
</script>

<template>
  <AppShell tool-name-key="tools.subnetCalculator">
    <template #controls>
      <span v-if="shareNotice" class="notice" role="status">{{ t('common.copied') }}</span>
    </template>

    <div class="layout">
      <div class="layout__main">
        <section class="controls">
          <NetworkInput v-model="networkText" @apply="calculator.setRoot(parseCidr($event))" />
          <ToolbarControls
            v-model:cloud-mode="calculator.cloudMode.value"
            :can-undo="calculator.canUndo.value"
            :can-redo="calculator.canRedo.value"
            @undo="calculator.undo()"
            @redo="calculator.redo()"
            @reset="calculator.reset()"
            @share="share()"
          />
        </section>

        <AdSlot placement="leaderboard" />

        <SubnetTable
          :design="calculator.design.value"
          :cloud-mode="calculator.cloudMode.value"
          @split="calculator.split($event)"
          @join="calculator.join($event)"
          @annotate="(cidr, patch) => calculator.annotate(cidr, patch)"
        />

        <p class="hint">
          {{ t('hint.splitJoin') }}
          <!-- Internal link to the reference page: it explains the Reserved
               addresses modes, and the two pages feed each other's authority. -->
          <a class="hint__link" :href="referenceHref">{{ t('reference.linkFromApp') }}</a>
        </p>
      </div>

      <AdSlot placement="sidebar" />
    </div>
  </AppShell>
</template>

<style scoped>
.layout {
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
}

.layout__main {
  flex: 1;
  min-width: 0;
}

.controls {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
}

.hint__link {
  margin-left: var(--space-2);
  white-space: nowrap;
}

.hint {
  margin-top: var(--space-3);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.notice {
  font-size: var(--text-sm);
  color: var(--color-success);
}
</style>
